import { t } from "elysia";
import { z } from "zod";

import {
	type LOBEnumType, type SubmissionStatus, type UnderwritingDecisionResult, type UnderwritingDecisionReviewStatus,
} from "@ca/db/schema";
import {
	type GateState, type PrepKind, type PrepState,
} from "@ca/db/schema/ledger";

import type { FlightProcessingCounts } from "./flights";
import type { Nullish } from "./util";

export interface PaginatedSubmissionAircraft {
	submissionId: string;
	aircraftId: string;
	aircraft: {
		make: string;
		model: string;
		year: string | null;
		tailNumber: string;
	};
}


export interface PaginatedSubmission {
	id: string;
	timestamp: Date;
	state: string | null;
	status: SubmissionStatus;
	lob: LOBEnumType;
	subject: string;
	inceptionDate: string | null;
	isNewPurchase: boolean | null;
	target: boolean | null;
	nextReminderDate: Date | null;
	assignedTo: {
		email: string | null;
		id: string | null;
		identity: {
			key: string;
			name: string | null;
			avatar: string | null;
		} | null;
	} | null;
	broker: {
		email: string;
		identity: {
			key: string;
			name: string | null;
		} | null;
		brokerage: {
			name: string | null;
			avatar: string | null;
			id: string;
		} | null;
	} | null;
	submissionsAircraft: PaginatedSubmissionAircraft[];
}

export const submissionNackReasons = {
	out_of_appetite_general: {
		label: "Out of appetite - general",
		subjectTemplate: "{insuredName} - Out of appetite",
		emailText: [
			"Thank you for submitting this opportunity. Following a comprehensive underwriting assessment, we have determined that the risk profile does not align with our current appetite and strategic focus. As such, we will not be offering terms at this time.",
			"We appreciate your continued engagement and welcome future submissions that fall within our preferred parameters.",
			"Thank you for considering Class A as a strategic market partner.",
		],
		status: "declined",
	},
	due_to_claims_history_loss_performance: {
		label: "Due to claims history / loss performance",
		subjectTemplate: "Declining to quote {insuredName} due to claims history / loss performance",
		emailText: [
			"We appreciate the opportunity to review this account. However, after evaluating the historical loss performance, we are unable to proceed due to underwriting constraints tied to adverse claims experience.",
			"We would be pleased to revisit this risk should the loss trajectory improve over time.",
			"Thank you for considering Class A as a strategic market partner.",
		],
		status: "declined",
	},
	due_to_operational_profile: {
		label: "Due to operational profile",
		subjectTemplate: "Declining to quote {insuredName} due to operational profile",
		emailText: [
			"Thank you for sharing this submission. The operational characteristics currently fall outside of our underwriting tolerance. We remain available to review future risks with operational structures more aligned to our appetite.",
			"Thank you for considering Class A as a strategic market partner.",
		],
		status: "declined",
	},
	due_to_aircraft_type_age: {
		label: "Due to aircraft type / age",
		subjectTemplate: "Declining to quote {insuredName} due to aircraft type / age",
		emailText: [
			"We have reviewed the submission, and due to internal underwriting restrictions related to aircraft make/model, age, or configuration, we are unable to proceed.",
			"Thank you for considering Class A as a strategic market partner.",
		],
		status: "declined",
	},
	already_fully_quoted_capacity_allocated: {
		label: "Already fully quoted / capacity allocated",
		subjectTemplate: "Capacity constraints regarding {insuredName}",
		emailText: [
			"Thank you for your submission.  Due to current capacity limits on the Hull/Liability limits, we cannot provide the full limit requested at this time.  If a quota share becomes an option, please advise and we will prioritize a re-evaluation.",
			"Thank you for considering Class A as a strategic market partner.",
		],
		status: "archived",
	},
	declining_but_open_to_future: {
		label: "Open to future (aka no action taken)",
		subjectTemplate: "{insuredName} - Open to future",
		emailText: [
			"While we are not in a position to engage on this particular submission, we remain interested in similar opportunities that may present differently in the future—particularly where there are changes in operational or risk structure.",
			"Thank you for considering Class A as a strategic market partner.",
		],
		status: "archived",
	},
	risk_submitted_outside_90_day_window: {
		label: "Risk submitted outside of 90 day window",
		subjectTemplate: "{insuredName} - Risk submitted outside of 90 day window",
		emailText: [
			"Thank you for this submitting this opportunity. Please note that the file has been received outside our standard 90-day quoting window. In line with our underwriting protocols, we are unable to initiate a review at this time.",
			"You're welcome to resubmit once the account falls within the applicable window. We'll be glad to revisit it then.",
			"Thank you for considering Class A as a strategic market partner.",
		],
		status: "archived",
	},
	incomplete_submission_key_underwriting_data_missing: {
		label: "Incomplete submission / key underwriting data missing",
		subjectTemplate: "{insuredName} - Incomplete submission",
		emailText: [
			"We appreciate the submission; however, we are unable to proceed with clearance and underwriting due to the absence of critical information required for risk evaluation. Given our current workflow prioritization, we must focus on complete and fully documented submissions. Should you be able to compile the outstanding data, we are happy to re-engage.",
			"Thank you for considering Class A as a strategic market partner.",
		],
		status: null,
	},
	already_involved: {
		label: "Already involved with another Agent/Broker",
		subjectTemplate: "{insuredName} - BOR required",
		emailText: [
			"Thank you for submitting this opportunity. However, we have already been approached regarding this risk by another agent/broker. To proceed with your submission, we will require a Broker of Record (BOR) letter signed by an officer of the insured.", 
			"We appreciate your interest in Class A and value the opportunity to be considered as a strategic market partner.",
		],
		status: "archived",
	},
	unlicensed_state: {
		label: "Unlicensed state",
		subjectTemplate: "{insuredName} - unable to write policy in the insured's state",
		emailText: [
			"Thank you for your submission. At this time, Class A is not yet approved to write business in the insured's domiciled state, though we are approved in many other states. If the insured can provide an alternative address, please feel free to resubmit and we will be glad to consider the risk.",
			"We will keep you informed as our state regulatory approvals progress and look forward to the opportunity to work with you in the future.",
			"We truly appreciate your interest in Class A.",
		],
		status: "archived",
	},
	vfd_invitation_reminder: {
		label: "VFD Invitation Reminder",
		subjectTemplate: "{insuredName} - VFD Invitation Reminder",
		emailText: [
			"We've noticed our insured has not yet been invited to join the Class A Virtual Flight Department Mobile App. In order to do so, we will need to send them an email with login credentials. Please advise the insured's and/or pilots' email so we can send out an invitation.",
			"Thank you.",
			"-The Class A Team",
		],
		status: null,
	},
	custom: {
		label: "Custom",
		subjectTemplate: "{insuredName} - Follow up",
		emailText: [],
		status: null,
	},
};

export const makeNackSubject = (insuredName: string, template: string) => {
	return template.replace("{insuredName}", insuredName);
};

const DAY_MS = 24 * 60 * 60 * 1000;

const isoDate = (date: Date) => date.toISOString().slice(0, 10);

export type QuoteSendDateRange = { max: string; min: string };

export const quoteSendDateRange = (
	inceptionDate: null | string | undefined,
	now: Date = new Date(),
): QuoteSendDateRange => {
	const min = isoDate(now);
	const inception = inceptionDate ? new Date(`${inceptionDate.slice(0, 10)}T00:00:00Z`) : null;
	if (!inception || Number.isNaN(inception.getTime())) {
		return { max: min, min };
	}
	const dayBefore = isoDate(new Date(inception.getTime() - DAY_MS));
	return { max: dayBefore > min ? dayBefore : min, min };
};

/** The picker reports the underwriter's local date, which trails UTC by up to a day in the evening. */
export const validateQuoteSendDate = (
	sendOn: string,
	range: QuoteSendDateRange,
): null | string => {
	if (sendOn > range.max) {
		return null;
	}
	const earliest = isoDate(new Date(new Date(`${range.min}T00:00:00Z`).getTime() - DAY_MS));
	if (sendOn < earliest) {
		return null;
	}
	return sendOn < range.min ? range.min : sendOn;
};

export type FollowUpDateRange = { max: null | string; min: string };

const utcMidnight = (isoDay: null | string | undefined): Date | null => {
	const parsed = isoDay ? new Date(`${isoDay.slice(0, 10)}T00:00:00Z`) : null;
	return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

export const followUpDateRange = (
	inceptionDate: null | string | undefined,
	now: Date = new Date(),
): FollowUpDateRange | null => {
	const min = isoDate(new Date(now.getTime() + DAY_MS));
	const inception = utcMidnight(inceptionDate);
	if (!inception) {
		return { max: null, min };
	}
	const dayBefore = isoDate(new Date(inception.getTime() - DAY_MS));
	return dayBefore >= min ? { max: dayBefore, min } : null;
};

export const FOLLOW_UP_LEAD_DAYS = [30] as const;

export const defaultFollowUpDates = (
	inceptionDate: null | string | undefined,
	now: Date = new Date(),
): string[] => {
	const range = followUpDateRange(inceptionDate, now);
	const inception = utcMidnight(inceptionDate);
	if (!range?.max || !inception) {
		return [];
	}
	return FOLLOW_UP_LEAD_DAYS
		.map(days => isoDate(new Date(inception.getTime() - days * DAY_MS)))
		.filter(date => date >= range.min)
		.sort();
};

export const validateFollowUpDates = (
	followUpOn: readonly string[],
	range: FollowUpDateRange,
): null | string[] => {
	const earliest = isoDate(new Date(new Date(`${range.min}T00:00:00Z`).getTime() - DAY_MS));
	const dates = new Set<string>();
	for (const picked of followUpOn) {
		const date = picked.slice(0, 10);
		if (date < earliest || (range.max !== null && date > range.max)) {
			return null;
		}
		dates.add(date < range.min ? range.min : date);
	}
	return dates.size > 0 ? [...dates].sort() : null;
};

export type DeferBlockedReason = "inception_too_close" | "no_broker_thread";

export const deferBlockedReason = (
	decision: { canDefer: boolean; inceptionDate: null | string },
	now: Date = new Date(),
): DeferBlockedReason | null => {
	if (!decision.canDefer) {
		return "no_broker_thread";
	}
	return followUpDateRange(decision.inceptionDate, now) ? null : "inception_too_close";
};

export const DEFER_BLOCKED_MESSAGES: Record<DeferBlockedReason, string> = {
	inception_too_close: "Inception is too close to schedule a follow-up",
	no_broker_thread: "This submission has no broker thread to follow up on",
};

export const resolveFollowUpDates = (
	decision: { canDefer: boolean; inceptionDate: null | string },
	followUpOn: readonly string[],
	now: Date = new Date(),
): { code: 400 | 409; message: string } | { dates: string[] } => {
	const blocked = deferBlockedReason(decision, now);
	const range = followUpDateRange(decision.inceptionDate, now);
	if (blocked || !range) {
		return { code: 409, message: DEFER_BLOCKED_MESSAGES[blocked ?? "inception_too_close"] };
	}
	const dates = validateFollowUpDates(followUpOn, range);
	if (!dates) {
		return {
			code: 400,
			message: `Pick follow-up dates from ${range.min}${range.max ? ` through ${range.max}` : ""}`,
		};
	}
	return { dates };
};

export type UnderwritingReviewStatus = "agree" | "disagree";

export const isTerminalReviewStatus = (
	status: UnderwritingDecisionReviewStatus,
): status is UnderwritingReviewStatus => status === "agree" || status === "disagree";

export const decisionInceptionDate = (decision: {
	quote?: { inceptionDate: null | string } | null;
	submission?: { inceptionDate: null | string } | null;
}) => decision.quote?.inceptionDate ?? decision.submission?.inceptionDate ?? null;

export const inceptionPassed = (
	inceptionDate: null | string | undefined,
	now: Date = new Date(),
) => Boolean(inceptionDate && inceptionDate.slice(0, 10) < isoDate(now));

export const reviewOutcome = (
	decision: UnderwritingDecisionResult,
	status: UnderwritingReviewStatus,
): UnderwritingDecisionResult => {
	if (status === "agree") {
		return decision;
	}
	return decision === "quote" ? "decline" : "quote";
};

export const reviewSendsQuote = (
	decision: { decision: UnderwritingDecisionResult; quoteId: null | string },
	status: UnderwritingReviewStatus,
) => reviewOutcome(decision.decision, status) === "quote" && Boolean(decision.quoteId);

export const quoteEmailText = [
	"Thank you for the opportunity to review this account. Our quote for {insuredName} ({tails}) is attached, with coverage effective {inceptionDate}.",
	"Please review the attached terms and let us know if you have any questions or would like to proceed with binding.",
	"Thank you for considering Class A as a strategic market partner.",
	"{underwriterName}",
];

export const makeQuoteEmailText = (values: {
	inceptionDate: string;
	insuredName: string;
	tails: string;
	underwriterName: string;
}) => quoteEmailText.map(paragraph => paragraph
	.replace("{inceptionDate}", values.inceptionDate)
	.replace("{insuredName}", values.insuredName)
	.replace(values.tails ? "{tails}" : " ({tails})", values.tails)
	.replace("{underwriterName}", values.underwriterName));

export const quoteEmailSubjects = {
	withoutTails: "Quote for {insuredName}",
	withTails: "Quote for {insuredName} ({tails})",
};

export const makeQuoteEmailSubject = (values: {
	insuredName: string;
	tails: string;
}) => (values.tails ? quoteEmailSubjects.withTails : quoteEmailSubjects.withoutTails)
	.replace("{insuredName}", values.insuredName)
	.replace("{tails}", values.tails);

export const quoteOutreachIntros = {
	withChannel: "We received your submission for {insuredName} through {brokerage}, and I'm reaching out directly with our quote.",
	withoutChannel: "We received your submission for {insuredName}, and I'm reaching out directly with our quote.",
};

export const makeQuoteOutreachText = (values: {
	brokerage: Nullish<string>;
	inceptionDate: string;
	insuredName: string;
	tails: string;
	underwriterName: string;
}) => {
	const brokerage = values.brokerage?.trim();
	const intro = (brokerage ? quoteOutreachIntros.withChannel : quoteOutreachIntros.withoutChannel)
		.replace("{insuredName}", values.insuredName)
		.replace("{brokerage}", brokerage ?? "");
	return [intro, ...makeQuoteEmailText(values)];
};

export const getNackStatusForReason =
	(reason: keyof typeof submissionNackReasons): SubmissionStatus | null => {
		return submissionNackReasons[reason].status as SubmissionStatus | null;
	};


// annoyingly, we need to manually add all the keys to keep the typing happy
const standardKeys = t.Union([
	t.Literal("out_of_appetite_general"),
	t.Literal("due_to_claims_history_loss_performance"),
	t.Literal("due_to_operational_profile"),
	t.Literal("incomplete_submission_key_underwriting_data_missing"),
	t.Literal("already_fully_quoted_capacity_allocated"),
	t.Literal("due_to_aircraft_type_age"),
	t.Literal("declining_but_open_to_future"),
	t.Literal("risk_submitted_outside_90_day_window"),
	t.Literal("already_involved"),
	t.Literal("unlicensed_state"),
	t.Literal("vfd_invitation_reminder"),
]);

export const SubmissionNackBody = t.Union([
	t.Object({
		reason: standardKeys,
		submissionId: t.String(),
	}),
	t.Object({
		reason: t.Literal("custom"),
		customText: t.String(),
		submissionId: t.String(),
	}),
]);

export const SubmissionCommentPayload = t.Object({
	comment: t.String(),
	submissionId: t.String(),
	approvedStatus: t.Optional(t.Union([
		t.Literal("approved"),
		t.Literal("rejected"),
	])),
	/** When true, do not record a comment_added action (e.g. recording transcript comments already have an upload action). */
	skipActionLog: t.Optional(t.Boolean()),
});

export type SubmissionCommentPayload = typeof SubmissionCommentPayload.static & { email: string };

export const SubmissionCommentUpdatePayload = t.Object({
	comment: t.String(),
	approvedStatus: t.Optional(t.Union([
		t.Literal("approved"),
		t.Literal("rejected"),
	])),
});
export type SubmissionCommentUpdatePayload =
	typeof SubmissionCommentUpdatePayload.static & { email: string };

export const SubmissionIndicationSentimentSchema = t.Union([
	t.Literal(1),
	t.Literal(2),
	t.Literal(3),
	t.Literal(4),
]);
export type SubmissionIndicationSentiment = typeof SubmissionIndicationSentimentSchema.static;

export const SubmissionIndicationPayload = t.Object({
	submissionId: t.String(),
	price: t.Number(),
	sentiment: SubmissionIndicationSentimentSchema,
	comment: t.Optional(t.Nullable(t.String())),
});
export type SubmissionIndicationPayload =
	typeof SubmissionIndicationPayload.static & { userId: string };

export const SubmissionIndicationUpdatePayload = t.Object({
	price: t.Number(),
	sentiment: SubmissionIndicationSentimentSchema,
	comment: t.Optional(t.Nullable(t.String())),
});
export type SubmissionIndicationUpdatePayload =
	typeof SubmissionIndicationUpdatePayload.static & { userId: string };

export const SubmissionIndicationReviewStatusSchema = t.Union([
	t.Literal("approved"),
	t.Literal("rejected"),
]);
export type SubmissionIndicationReviewStatus =
	typeof SubmissionIndicationReviewStatusSchema.static;

export const SubmissionIndicationReviewPayload = t.Object({
	status: SubmissionIndicationReviewStatusSchema,
	reason: t.Optional(t.Nullable(t.String())),
});
export type SubmissionIndicationReviewPayload =
	typeof SubmissionIndicationReviewPayload.static & { userId: string };

export const UnderwritingDecisionPublishSchema = t.Object({
	action: t.Optional(t.Union([t.Literal("quote-send"), t.Literal("follow-up")])),
	decisionId: t.String(),
});

export const ParterSubmissionPublishSchema = t.Object({
	submissionId: t.String(),
});

export const SubmissionCreatedPublishSchema = t.Object({
	submissionId: t.String(),
});

export const SubmissionGateCheckSchema = t.Object({
	submissionId: t.String({ format: "uuid" }),
});
export type SubmissionGateCheck = typeof SubmissionGateCheckSchema.static;

/** Not `underwriting-decisions`, which carries a decision that already exists; this one says make it. */
export const UnderwritingDecisionRunSchema = t.Object({
	gateId: t.String({ format: "uuid" }),
	submissionId: t.String({ format: "uuid" }),
});
export type UnderwritingDecisionRun = typeof UnderwritingDecisionRunSchema.static;

export const PartnerSubmissionGetSchema = t.Object({
	id: t.String(),
	timestamp: t.Union([t.String(), t.Date()]),
	renewalDate: t.Nullable(t.String({
		format: "date",
	})),
	inceptionDate: t.Nullable(t.String({
		format: "date",
	})),
	status: t.String(),
	referenceId: t.Nullable(t.String()),
});

export const PartnerSubmissionPaginationResponseSchema = t.Object({
	submissions: t.Array(PartnerSubmissionGetSchema),
	pagination: t.Object({
		total: t.Number(),
		remaining: t.Number(),
		offset: t.Number(),
		limit: t.Number(),
		sort: t.Union([t.Literal("asc"), t.Literal("desc")]),
	}),
});

const SubmissionCreatedPayloadSchema = z.object({
	submissionId: z.string(),
});

export const checkSubmissionCreatedPayload = (payload: unknown) => {
	return SubmissionCreatedPayloadSchema.safeParse(payload);
};

export const SubmissionReminderPayloadSchema = t.Object({
	reminderDate: t.String({ format: "date-time" }),
	channels: t.Array(t.Union([t.Literal("slack"), t.Literal("email")]), {
		minItems: 1,
		default: ["slack", "email"],
	}),
});


export const sentimentOptions = [
	{
		label: "Tepid",
		description: "Low conviction that this wins.",
		color: "error",
		emote: "🤷🏻‍♂️",
	},
	{
		label: "Interested",
		description: "Possible, but not confident.",
		color: "neutral",
		emote: "🤔",
	},
	{
		label: "Optimistic",
		description: "Confident that this gets the business.",
		color: "info",
		emote: "🙂",
	},
	{
		label: "Aggressive",
		description: "Pursuing the business aggressively.",
		color: "success",
		emote: "🤩",
	},
] as const;


export const PilotCompetencyLevel = t.Integer({
	minimum: 1,
	maximum: 10,
});

export const PilotCompetencyPayload = t.Object({
	submissionId: t.String(),
	level: PilotCompetencyLevel,
	comment: t.Optional(t.Nullable(t.String())),
});

export type PilotCompetencyPayload =
    typeof PilotCompetencyPayload.static & { userEmail: string; userId: string };

export type SubmissionProcessingAircraft = {
	flights: FlightProcessingCounts;
	flightsProcessed: number;
	flightsTotal: number;
	pullsPending: number;
	tailNumber: string;
};

export type SubmissionProcessingStatus = {
	active: boolean;
	aircraft: SubmissionProcessingAircraft[];
	blockedAt: null | "flights" | "preps" | "pulls" | "reports";
	deadlineAt: null | string;
	decisionCandidate: boolean;
	flightsProcessed: number;
	flightsTotal: number;
	logbook: null | { completed: number; expected: number };
	outcome: "cancelled" | "complete" | "failed" | "skipped" | "working";
	preps: { kind: PrepKind; state: PrepState }[];
	pullsPending: number;
	reason: null | string;
	receivedAt: null | string;
	reportsUnresolved: number;
	state: GateState | null;
};
